<template>
    <div class="ct-bg-1--alt py-8 lg:py-32 px-5 lg:px-4 lg:shadow-lg">
        <div v-if="reviewConfirmError" class="mx-auto w-full max-w-[400px] space-y-6 ct-text-error">
            {{ reviewConfirmError }}
        </div>
        <div v-else class="mx-auto w-full max-w-[400px] space-y-6">
            <p class="text-lg ct-text-1 font-medium">
                {{
                    isReviewConfirmation
                        ? $t("r_love.review_form.title_confirm")
                        : $t("r_love.review_form.title")
                }}
            </p>
            <div class="space-y-2">
                <FormLabel>{{ $t("r_love.rating") }}</FormLabel>
                <StarsPicker v-model="review.rate" />
            </div>

            <FormInput
                :title="$t('shared.first_last_name')"
                v-model="review.name"
                @blur="v$.name.$touch"
                :error="v$.name.$errors?.[0]?.$message"
                required
            />

            <div class="space-y-2">
                <FormLabel>{{ $t("r_love.review.review_type") }}</FormLabel>
                <FormSwitch v-model="review.type" :options="reviewTypes" />
            </div>
            <template v-if="review.type === REVIEW_TYPE.text">
                <FormRichText
                    ref="content"
                    :title="$t('r_love.review.review')"
                    buttonList="compact"
                    v-model="review.content"
                    @blur="v$.content.$touch"
                    :error="v$.content.$errors?.[0]?.$message"
                    required
                />
                <div class="space-y-2">
                    <FormLabel>{{ $t("r_love.review.media") }}</FormLabel>
                    <FormSwitch v-model="uploadType" :options="uploadTypes" />

                    <FormFiles
                        v-if="uploadType === 'upload'"
                        v-model="imageFiles"
                        accept="image/*"
                        :labels="{
                            clickToUpload: $t('shared.uploader.click_to_upload'),
                            dragAndDrop: $t('shared.uploader.drag_and_drop'),
                            fileInfo: $t('shared.uploader.file_details'),
                        }"
                        :maxSize="10000"
                        :errorMessages="{
                            invalidSize: $t('shared.invalid.file_size_over'),
                            invalidType: $t('shared.invalid.file_type'),
                        }"
                        @fileSelected="handleUploadImage"
                        @fileRemoved="handleRemoveImage"
                    />
                    <div class="space-y-2" v-if="uploadType === 'gif'">
                        <FormInput v-model="gifQuery" @keyup.enter.native="handleGifSearch" />
                        <ButtonSecondaryLg class="w-full" @click="handleGifSearch">{{
                            $t("r_love.review.search_gif")
                        }}</ButtonSecondaryLg>
                        <CardsGridGallery
                            v-if="gifCollection.length"
                            :images="gifCollection"
                            pickMode
                            @pick="handleGifPicked"
                        />
                    </div>
                    <CardsGridGallery
                        v-if="review.gifs?.length"
                        :images="review.gifs"
                        @remove="handleGifRemove"
                    />
                </div>
            </template>
            <template v-if="review.type === REVIEW_TYPE.video">
                <c-video
                    class="w-full"
                    :video="review.video"
                    :editMode="true"
                    :withBorder="wall.id && !wall.border"
                    :borderColor="wall.borderColor"
                    @uploadVideo="uploadVideo"
                    @removeVideo="removeVideo"
                    @blur="v$.video.$touch"
                    :error="v$.video.$errors?.[0]?.$message"
                />
            </template>

            <FormInput
                v-if="wall.askForEmail"
                :required="Boolean(wall.emailRequired)"
                :title="$t('shared.email')"
                v-model="review.authorEmail"
                @blur="v$.authorEmail.$touch"
                :error="v$.authorEmail.$errors?.[0]?.$message"
                :tooltip="emailInfo"
            />

            <template v-if="!wall.hidePositionAndWww">
                <FormInput :title="$t('shared.position_company')" v-model="review.position" />

                <FormInputPreText
                    :title="$t('shared.website')"
                    v-model="testimonialWWW"
                    prefix="https://"
                />
            </template>

            <FormAvatar
                :title="$t('shared.avatar')"
                :uploadLabel="$t('shared.upload')"
                :deleteLabel="$t('shared.delete')"
                :file="review.photo"
                @loadFile="handleUploadAvatar"
            />

            <FormCheck
                :label="$t('r_love.review_form.permission')"
                v-model="review.permissionGranted"
                required
                @blur="v$.permissionGranted.$touch"
                :error="v$.permissionGranted.$errors?.[0]?.$message"
            />
            <ButtonPrimaryLg
                :style="{
                    backgroundColor: wall.formColors?.buttonBackground,
                    color: wall.formColors?.buttonText,
                    borderColor: wall.formColors?.buttonBackground,
                }"
                class="w-full"
                :disabled="isSending"
                @click="isReviewConfirmation ? handleConfirmReview() : handleSubmitReview()"
            >
                {{
                    isReviewConfirmation
                        ? $t("r_love.review_form.confirm_review")
                        : $t("r_love.review_form.submit_review")
                }}
            </ButtonPrimaryLg>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex";
import { handleActionClick } from "@/shared/utils";
import {
    FormInput,
    FormInputPreText,
    FormLabel,
    FormRichText,
    FormFiles,
    FormSwitch,
    FormCheck,
    FormAvatar,
} from "@easy/ui/forms";
import { ButtonPrimaryLg, ButtonSecondaryLg } from "@easy/ui/buttons";
import { StarsPicker } from "@easy/ui/stars";
import { CardsGridGallery } from "@easy/ui/cards";
import { uploadService } from "@/apps/love/services/upload.service";
import { testimonialService } from "@/apps/love/services/testimonial.service";
import { computed, reactive } from "vue";
import { useFormValidation } from "@/shared/utils/forms/useFormValidation";
import { email, minLength, required, mustBeChecked } from "@/shared/utils/forms/i18n-vuelidators";
import cVideo from "@/views/shared/cVideo.vue";
import { makeEmptyReview, REVIEW_TYPE } from "@/apps/love/model/Review";
import { mapMutations, mapActions } from "vuex";
import { cloneDeep } from "@/shared/utils/helpers";
import { useReviewConfirmation } from "@/views/tools/love/form/useReviewConfirmation";
import { i18n } from "@/i18n";

export default {
    name: "FormRight",
    components: {
        FormInput,
        FormInputPreText,
        FormLabel,
        FormRichText,
        FormFiles,
        FormSwitch,
        StarsPicker,
        FormCheck,
        FormAvatar,
        ButtonPrimaryLg,
        ButtonSecondaryLg,
        CardsGridGallery,
        cVideo,
    },
    props: {
        wall: {
            type: Object,
            default: () => ({}),
        },
    },
    setup(props) {
        const review = reactive(makeEmptyReview());
        const { isReviewConfirmation, reviewConfirmError, fireQuickConfirmation } =
            useReviewConfirmation(props, review);
        const rules = computed(() => {
            const alwaysRules = {
                name: { required },
                permissionGranted: { mustBeChecked },
            };
            switch (review.type) {
                case REVIEW_TYPE.text:
                    alwaysRules.content = { required, minLength: minLength(5) };
                    break;
                case REVIEW_TYPE.video:
                    alwaysRules.video = { required };
                    break;
            }
            if (props.wall.askForEmail) {
                alwaysRules.authorEmail = {
                    email,
                    ...(props.wall.emailRequired ? { required } : {}),
                };
            }
            return alwaysRules;
        });
        const { v$, validateForm, resetFrom } = useFormValidation(rules, review);
        return {
            review,
            v$,
            validateForm,
            resetFrom,
            isReviewConfirmation,
            reviewConfirmError,
            fireQuickConfirmation,
            REVIEW_TYPE,
        };
    },
    data() {
        return {
            gifQuery: "",
            gifCollection: [],
            imageFiles: [],
            uploadType: "upload",
            isSending: false,
        };
    },
    computed: {
        ...mapState("shared/view", ["preview"]),

        testimonialWWW: {
            get() {
                return this.review.www;
            },
            set(value) {
                const regex = /^(http|https):\/\/(.*)/gm;

                this.review.www = value.replace(regex, "$2");
            },
        },
        uploadTypes() {
            return [
                {
                    label: this.$t("shared.upload"),
                    value: "upload",
                },
                {
                    label: this.$t("r_love.review.search_gif"),
                    value: "gif",
                },
            ];
        },
        reviewTypes() {
            return [
                {
                    label: this.$t("r_love.review.text_review"),
                    value: "text",
                },
                {
                    label: this.$t("r_love.review.video_review"),
                    value: "video",
                },
            ];
        },
        emailInfo() {
            return this.wall.emailRequired
                ? this.$t("r_love.review_form.email_required_info")
                : this.$t("r_love.review_form.email_optional_info");
        },
    },
    methods: {
        ...mapMutations(["showModal"]),
        ...mapActions("alert", ["showErrorMessage"]),
        async handleUploadAvatar(file) {
            if (this.preview) {
                handleActionClick();
                return;
            }

            if (file === null) {
                return (this.review.photo = "");
            }
            try {
                const { avatar } = await uploadService.testimonials.avatarForNew({ file });
                this.review.photo = avatar;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        async handleUploadImage(file) {
            if (this.preview) {
                handleActionClick();
                return;
            }

            try {
                const { image } = await uploadService.testimonials.image({ file });
                this.review.images.push(image);
                file.url = image;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        async handleRemoveImage(file) {
            const { images } = this.review;
            const idx = images.indexOf(file.url || file.preview);
            if (idx !== -1) {
                images.splice(idx, 1);
            }
        },
        async handleGifSearch() {
            if (this.preview) {
                handleActionClick();
                return;
            }
            try {
                this.gifCollection = await testimonialService.searchForGifs(this.gifQuery);
            } catch (e) {
                return [];
            }
        },
        handleGifPicked(gif) {
            this.review.gifs.push(gif);
            this.gifQuery = "";
            this.gifCollection = [];
        },
        handleGifRemove(gif) {
            this.review.gifs = this.review.gifs.filter((url) => url !== gif);
        },
        async checkAndPrepareReviewToSend() {
            const isOk = await this.validateForm();
            if (!isOk) {
                this.showErrorMessage({
                    title: this.$t("shared.invalid.some_form_fields"),
                });

                return;
            }
            this.isSending = true;
            const testimonialCopy = cloneDeep(this.review);
            // hacky way for now :( [we do not have field: permissionGranted in testimonial]
            delete testimonialCopy.permissionGranted;
            return testimonialCopy;
        },
        async handleSubmitReview() {
            if (this.preview) {
                handleActionClick();
                return;
            }
            const testimonial = await this.checkAndPrepareReviewToSend();
            if (!testimonial) {
                return;
            }
            const { id, publishAutomatically } = this.wall;
            const result = await testimonialService.createNew(
                {
                    ...testimonial,
                    wallId: id,
                    isPublished: Boolean(publishAutomatically),
                },
                { lang: i18n.global.locale.value }
            );
            if (result) {
                await this.resetFormData();
                this.showSuccessModal(this.$t("r_love.review_form.created"));
            }
        },
        async handleConfirmReview() {
            if (this.preview) {
                handleActionClick();
                return;
            }
            const testimonial = await this.checkAndPrepareReviewToSend();
            if (!testimonial) {
                return;
            }
            const result = await testimonialService.confirm({
                id: testimonial.id,
                testimonial,
            });
            if (result) {
                this.showSuccessModal(this.$t("r_love.review_form.confirmed"));
            }
        },
        // Firing from fireQuickConfirmation WATCHER:
        async handleQuickConfirmation() {
            if (this.reviewConfirmError) {
                return;
            }
            this.isSending = true;
            const result = await testimonialService.confirm({
                id: this.review.id,
                testimonial: this.review,
            });
            if (result) {
                this.showSuccessModal(this.$t("r_love.review_form.confirmed"));
            }
        },
        async resetFormData() {
            this.isSending = false;
            await this.resetFrom();
            // @fix[EAS-1918] content ref might not be present if this is a video review.
            if (this.$refs.content) {
                // ATTENTION: this is crucial to make v-model work again!
                this.$refs.content.markPristine?.();
            }
            Object.assign(this.review, makeEmptyReview());
        },
        showSuccessModal(title) {
            const { redirectButton, redirectUrl } = this.wall;
            this.showModal({
                type: "success",
                title,
                actionBtn: redirectButton,
                callback: () => {
                    window.location.href = redirectUrl;
                },
            });
        },
        async uploadVideo(file) {
            if (this.preview) {
                handleActionClick();
                return;
            }
            try {
                const { video } = await uploadService.testimonials.video({ file });
                this.review.video = video;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        removeVideo() {
            this.review.video = "";
        },
    },
    watch: {
        fireQuickConfirmation: {
            handler(value) {
                if (value) {
                    this.$nextTick(() => this.handleQuickConfirmation());
                }
            },
            immediate: true,
        },
    },
};
</script>
